import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { 
  FileCode, 
  Calendar, 
  Download, 
  Database,
  FileText,
  DollarSign,
  ShieldCheck
} from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";

const SaftContabilidade = () => {
  const { user, empresa } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saftData, setSaftData] = useState(null);

  const [filtros, setFiltros] = useState({
    inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  const fetchSaftPreview = async () => {
    try {
      setLoading(true);
      const response = await api.post("/caixa/saft", {
        empresa_id: empresa?.id || user?.empresa_id,
        inicio: filtros.inicio,
        fim: filtros.fim
      });
      setSaftData(response.data?.data || response.data);
    } catch (err) {
      console.error("Erro ao carregar pré-visualização SAF-T:", err);
      notify("Erro ao carregar dados para o SAF-T.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa?.id || user?.empresa_id) {
      fetchSaftPreview();
    }
  }, [empresa?.id, user?.empresa_id]);

  const handleDateChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchSaftPreview();
  };

  const handleDownloadXML = async () => {
    try {
      setExporting(true);
      const response = await api.post("/caixa/saft", {
        empresa_id: empresa?.id || user?.empresa_id,
        inicio: filtros.inicio,
        fim: filtros.fim
      });
      
      const payload = response.data?.data || response.data;
      const xmlString = generateXMLString(payload);

      const blob = new Blob([xmlString], { type: "application/xml;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SAFT_Contabilidade_${filtros.inicio}_${filtros.fim}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      notify("Ficheiro SAF-T XML descarregado com sucesso.", "success");
    } catch (err) {
      console.error(err);
      notify("Erro ao gerar o ficheiro XML.", "error");
    } finally {
      setExporting(false);
    }
  };

  // Função auxiliar para estruturar o XML de contabilidade/faturação
  const generateXMLString = (data) => {
    const header = data?.header || {};
    const invoices = data?.invoices || [];

    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<AuditFile xmlns="urn:OECD:StandardAuditFile-Tax:AO_1.03">\n`;
    xml += `  <Header>\n`;
    xml += `    <FiscalYear>${new Date(filtros.inicio).getFullYear()}</FiscalYear>\n`;
    xml += `    <StartDate>${filtros.inicio}</StartDate>\n`;
    xml += `    <EndDate>${filtros.fim}</EndDate>\n`;
    xml += `    <CurrencyCode>AOA</CurrencyCode>\n`;
    xml += `    <CompanyTaxID>${empresa?.nif || '999999999'}</CompanyTaxID>\n`;
    xml += `    <CompanyName>${empresa?.nome || 'Empresa Hotelaria'}</CompanyName>\n`;
    xml += `  </Header>\n`;
    xml += `  <SourceDocuments>\n`;
    xml += `    <SalesInvoices>\n`;
    xml += `      <NumberOfEntries>${header.total_documentos || 0}</NumberOfEntries>\n`;
    xml += `      <TotalDebit>${header.total_geral || 0}</TotalDebit>\n`;
    xml += `      <TotalCredit>0.00</TotalCredit>\n`;

    invoices.forEach(inv => {
      xml += `      <Invoice>\n`;
      xml += `        <InvoiceNo>${inv.invoice_no}</InvoiceNo>\n`;
      xml += `        <InvoiceDate>${inv.invoice_date}</InvoiceDate>\n`;
      xml += `        <InvoiceType>${inv.invoice_type}</InvoiceType>\n`;
      xml += `        <DocumentStatus>\n`;
      xml += `          <InvoiceStatus>${inv.status}</InvoiceStatus>\n`;
      xml += `          <SourceID>System</SourceID>\n`;
      xml += `        </DocumentStatus>\n`;
      xml += `        <CustomerID>${inv.customer_id || 'CF'}</CustomerID>\n`;
      xml += `        <DocumentTotals>\n`;
      xml += `          <TaxPayable>${inv.tax_total}</TaxPayable>\n`;
      xml += `          <NetTotal>${inv.net_total}</NetTotal>\n`;
      xml += `          <GrossTotal>${inv.gross_total}</GrossTotal>\n`;
      xml += `        </DocumentTotals>\n`;
      xml += `      </Invoice>\n`;
    });

    xml += `    </SalesInvoices>\n`;
    xml += `  </SourceDocuments>\n`;
    xml += `</AuditFile>`;

    return xml;
  };

  const header = saftData?.header || { total_documentos: 0, total_incidencia: 0, total_iva: 0, total_geral: 0 };
  const invoices = saftData?.invoices || [];

  return (
    <ResourcePage 
      title="SAF-T Contabilidade (XML)" 
      breadcrumb={["Gestão Financeira", "SAF-T XML"]}
      actions={(
        <button 
          type="button" 
          className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2 px-3 shadow-sm"
          onClick={handleDownloadXML}
          disabled={exporting || loading}
        >
          <Download size={16} /> {exporting ? "A gerar XML..." : "Descarregar SAF-T XML"}
        </button>
      )}
    >
      {/* Filtro de Período */}
      <div className="card bg-white border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <form onSubmit={handleFilterSubmit} className="row align-items-center g-3">
            <div className="col-md-auto">
              <span className="fw-semibold text-muted d-flex align-items-center gap-1">
                <Calendar size={18} /> Período Fiscal:
              </span>
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="inicio" 
                value={filtros.inicio} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="fim" 
                value={filtros.fim} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-auto">
              <button 
                type="submit" 
                className="btn btn-dark btn-sm px-4" 
                disabled={loading}
              >
                {loading ? "A processar..." : "Atualizar Pré-visualização"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Cartões Informativos do SAF-T */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-primary bg-primary-subtle p-2 rounded d-inline-flex mb-3">
                <FileText size={20} />
              </div>
              <p className="text-muted mb-1 small">Total Documentos</p>
              <h4 className="mb-0 fw-bold">{header.total_documentos}</h4>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-success bg-success-subtle p-2 rounded d-inline-flex mb-3">
                <DollarSign size={20} />
              </div>
              <p className="text-muted mb-1 small">Total Incidência (Net)</p>
              <h4 className="mb-0 text-success fw-bold">{Number(header.total_incidencia).toLocaleString()} Kz</h4>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-info bg-info-subtle p-2 rounded d-inline-flex mb-3">
                <Database size={20} />
              </div>
              <p className="text-muted mb-1 small">Total IVA (Impostos)</p>
              <h4 className="mb-0 text-info fw-bold">{Number(header.total_iva).toLocaleString()} Kz</h4>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-warning bg-warning-subtle p-2 rounded d-inline-flex mb-3">
                <ShieldCheck size={20} />
              </div>
              <p className="text-muted mb-1 small">Total Geral (Gross)</p>
              <h4 className="mb-0 fw-bold text-dark">{Number(header.total_geral).toLocaleString()} Kz</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Pré-visualização das Faturas no SAF-T */}
      <div className="card bg-white border-0 shadow-sm">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="card-title mb-0 fs-5 d-flex align-items-center gap-2">
              <FileCode size={20} className="text-primary" /> Pré-visualização das Entradas do SAF-T
            </h4>
            <span className="text-muted small">Estrutura pronta para exportação fiscal</span>
          </div>

          <div className="table-responsive">
            <table className="table table-centered table-hover mb-0 align-middle">
              <thead>
                <tr className="text-muted fs-7">
                  <th>Nº do Documento</th>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Incidência</th>
                  <th>IVA</th>
                  <th>Total Bruto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length > 0 ? (
                  invoices.map((inv, idx) => (
                    <tr key={idx}>
                      <td><span className="fw-semibold">{inv.invoice_no}</span></td>
                      <td>{inv.invoice_date}</td>
                      <td><span className="badge bg-light text-dark">{inv.invoice_type}</span></td>
                      <td>{Number(inv.net_total).toLocaleString()} Kz</td>
                      <td>{Number(inv.tax_total).toLocaleString()} Kz</td>
                      <td className="fw-bold">{Number(inv.gross_total).toLocaleString()} Kz</td>
                      <td>
                        <span className={`badge ${inv.status === "Validado" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"}`}>
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">Nenhum registo fiscal encontrado para o período selecionado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ResourcePage>
  );
};

export default SaftContabilidade;